/**
 * Company Registration Section Component
 *
 * Features:
 * - Benefits list with checkmarks
 * - Interactive fee calculator with sliders (fetches default fees from backend)
 * - Real-time calculations
 * - Monthly projections
 * - CTA opens company registration modal (same behavior as /company/register)
 * - Scroll animations
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './Modal';
import CompanyRegisterModalContent from '../auth/CompanyRegisterModalContent';

/** Default fee config from backend (default-fees). Used when no override. */
const DEFAULT_FEE_FALLBACK = {
  bank_fee_percent: 2.5,
  bank_fee_fixed: 0.5,
  platform_fee_percent: 1.5,
  platform_fee_fixed: 0.2,
};

interface DefaultFeeConfig {
  bank_fee_percent?: number | null;
  bank_fee_fixed?: number | null;
  platform_fee_percent?: number | null;
  platform_fee_fixed?: number | null;
}

interface FeeBreakdown {
  customerPays: number;
  companyReceives: number;
  bankFee: number;
  platformFee: number;
  totalFees: number;
  monthlyRevenue: number;
  monthlyFees: number;
  monthlyNet: number;
}

function getCalculatorApiBase(): string {
  const env = (import.meta.env.VITE_API_URL ?? '').trim();
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return '/api';
  }
  return env ? `${env}/api` : '/api';
}

const CompanyRegistrationSection: React.FC = () => {
  const { t } = useTranslation('portal');
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);

  // Default fee config from backend (homepage always uses default, never company override)
  const [feeConfig, setFeeConfig] = useState<DefaultFeeConfig | null>(null);
  const [feeConfigLoading, setFeeConfigLoading] = useState(true);
  const [feeConfigError, setFeeConfigError] = useState<string | null>(null);

  // Calculator state
  const [transactionAmount, setTransactionAmount] = useState(500);
  const [monthlyTransactions, setMonthlyTransactions] = useState(100);
  const [breakdown, setBreakdown] = useState<FeeBreakdown | null>(null);

  // Fetch default fees from backend (public endpoint, no auth)
  useEffect(() => {
    let cancelled = false;
    setFeeConfigError(null);
    setFeeConfigLoading(true);
    const base = getCalculatorApiBase();
    fetch(`${base}/calculator/default-fees`)
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText || 'Failed to load fees');
        return res.json();
      })
      .then((data: DefaultFeeConfig) => {
        if (!cancelled) setFeeConfig(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setFeeConfigError(err instanceof Error ? err.message : 'Unknown error');
          setFeeConfig(DEFAULT_FEE_FALLBACK);
        }
      })
      .finally(() => {
        if (!cancelled) setFeeConfigLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const effectiveConfig = feeConfig ?? DEFAULT_FEE_FALLBACK;
  const bankPercent = effectiveConfig.bank_fee_percent ?? DEFAULT_FEE_FALLBACK.bank_fee_percent;
  const bankFixed = effectiveConfig.bank_fee_fixed ?? DEFAULT_FEE_FALLBACK.bank_fee_fixed;
  const platformPercent = effectiveConfig.platform_fee_percent ?? DEFAULT_FEE_FALLBACK.platform_fee_percent;
  const platformFixed = effectiveConfig.platform_fee_fixed ?? DEFAULT_FEE_FALLBACK.platform_fee_fixed;

  // Calculate fees in real-time (both fees on full amount; all rounded to 2 decimals)
  useEffect(() => {
    const round2 = (n: number) => Math.round(n * 100) / 100;
    const bPct = Number(bankPercent) / 100;
    const pPct = Number(platformPercent) / 100;
    const bFix = Number(bankFixed);
    const pFix = Number(platformFixed);
    const bankFee = round2(transactionAmount * bPct + bFix);
    const platformFee = round2(transactionAmount * pPct + pFix);
    const totalFees = round2(bankFee + platformFee);
    const companyReceives = round2(transactionAmount - bankFee - platformFee);

    const monthlyRevenue = transactionAmount * monthlyTransactions;
    const monthlyFees = totalFees * monthlyTransactions;
    const monthlyNet = companyReceives * monthlyTransactions;

    setBreakdown({
      customerPays: transactionAmount,
      companyReceives,
      bankFee,
      platformFee,
      totalFees,
      monthlyRevenue,
      monthlyFees,
      monthlyNet,
    });
  }, [transactionAmount, monthlyTransactions, bankPercent, bankFixed, platformPercent, platformFixed]);

  // Intersection Observer for scroll animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const benefits = [
    t('landing.companySection.benefit1'),
    t('landing.companySection.benefit2'),
    t('landing.companySection.benefit3'),
    t('landing.companySection.benefit4'),
    t('landing.companySection.benefit5'),
  ];

  return (
    <section
      ref={sectionRef}
      className={`py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-indigo-50 transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Column: Benefits and CTA */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                {t('landing.companySection.title')}
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                {t('landing.companySection.subtitle')}
              </p>
            </div>

            {/* Benefits List */}
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-3 transition-all duration-500 delay-${index * 100}`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <span className="text-lg text-slate-700">{benefit}</span>
                </div>
              ))}
            </div>

            {/* CTA Button - opens registration modal */}
            <div className="pt-6 flex flex-col items-center md:items-start">
              <button
                type="button"
                onClick={() => setRegisterModalOpen(true)}
                data-testid="link-company-register"
                className="px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all transform hover:scale-105 shadow-lg inline-flex items-center justify-center"
              >
                {t('landing.companySection.ctaRegister')}
              </button>
              <p className="mt-4 text-sm text-slate-500 text-center md:text-left">
                {t('landing.companySection.noCosts')}
              </p>
            </div>

            <Modal
              isOpen={registerModalOpen}
              onClose={() => setRegisterModalOpen(false)}
              title={t('landing.companySection.modalTitle')}
              size="md"
              showCloseButton={true}
            >
              <div data-testid="modal-company-register" className="bg-white rounded-xl border border-slate-200 p-4">
                <CompanyRegisterModalContent
                  onClose={() => setRegisterModalOpen(false)}
                  onSuccess={() => setRegisterModalOpen(false)}
                />
              </div>
            </Modal>
          </div>

          {/* Right Column: Interactive Calculator */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              {t('landing.calculator.title')}
            </h3>
            <p className="text-slate-600 mb-8">
              {t('landing.calculator.subtitle')}
            </p>
            {feeConfigLoading && (
              <p className="text-sm text-slate-500 mb-4">{t('landing.calculator.loading')}</p>
            )}
            {feeConfigError && (
              <p className="text-sm text-amber-600 mb-4" role="alert">{t('landing.calculator.error')}</p>
            )}

            {/* Transaction Amount Slider */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm font-medium text-slate-700">
                  {t('landing.calculator.transactionAmount')}
                </label>
                <span className="text-lg font-bold text-blue-600">
                  {transactionAmount.toFixed(2)} KM
                </span>
              </div>
              <input
                type="range"
                min="50"
                max="5000"
                step="10"
                value={transactionAmount}
                onChange={(e) => setTransactionAmount(Number(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>50 KM</span>
                <span>5,000 KM</span>
              </div>
            </div>

            {/* Monthly Transactions Slider */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm font-medium text-slate-700">
                  {t('landing.calculator.monthlyTransactions')}
                </label>
                <span className="text-lg font-bold text-blue-600">
                  {monthlyTransactions}
                </span>
              </div>
              <input
                type="range"
                min="1"
                max="1000"
                step="1"
                value={monthlyTransactions}
                onChange={(e) => setMonthlyTransactions(Number(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>1</span>
                <span>1,000</span>
              </div>
            </div>

            {/* Fee Breakdown */}
            {breakdown && (
              <div className="space-y-6">
                {/* Per Transaction Breakdown */}
                <div className="border-t border-slate-200 pt-6">
                  <h4 className="text-sm font-semibold text-slate-700 mb-4 uppercase tracking-wide">
                    {t('landing.calculator.perTransaction')}
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">{t('landing.calculator.customerPays')}</span>
                      <span className="text-lg font-bold text-slate-900">
                        {breakdown.customerPays.toFixed(2)} KM
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-500 pl-4">
                        {t('landing.calculator.bankFee')}
                      </span>
                      <span className="text-slate-600">
                        {breakdown.bankFee.toFixed(2)} KM
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-500 pl-4">
                        ({Number(bankPercent).toFixed(1)}% + {Number(bankFixed).toFixed(2)} KM)
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-500 pl-4">
                        {t('landing.calculator.platformFee')}
                      </span>
                      <span className="text-slate-600">
                        {breakdown.platformFee.toFixed(2)} KM
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-500 pl-4">
                        ({Number(platformPercent).toFixed(1)}% + {Number(platformFixed).toFixed(2)} KM)
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                      <span className="text-slate-600">{t('landing.calculator.totalFees')}</span>
                      <span className="font-semibold text-red-600">
                        -{breakdown.totalFees.toFixed(2)} KM
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                      <span className="font-semibold text-slate-800">
                        {t('landing.calculator.companyReceives')}
                      </span>
                      <span className="text-xl font-bold text-green-600">
                        {breakdown.companyReceives.toFixed(2)} KM
                      </span>
                    </div>
                  </div>
                </div>

                {/* Monthly Projection */}
                <div className="border-t border-slate-200 pt-6 bg-blue-50 -mx-8 px-8 -mb-8 pb-8 rounded-b-2xl">
                  <h4 className="text-sm font-semibold text-slate-700 mb-4 uppercase tracking-wide">
                    {t('landing.calculator.monthlyProjection')}
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">{t('landing.calculator.totalRevenue')}</span>
                      <span className="text-lg font-bold text-slate-900">
                        {breakdown.monthlyRevenue.toFixed(2)} KM
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">{t('landing.calculator.totalFees')}</span>
                      <span className="font-semibold text-red-600">
                        -{breakdown.monthlyFees.toFixed(2)} KM
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-blue-200">
                      <span className="text-lg font-bold text-slate-800">
                        {t('landing.calculator.netIncome')}
                      </span>
                      <span className="text-2xl font-bold text-green-600">
                        {breakdown.monthlyNet.toFixed(2)} KM
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-3">
                      {t('landing.calculator.estimate')} {monthlyTransactions} transakcija po{' '}
                      {transactionAmount.toFixed(2)} KM
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyRegistrationSection;
